import { useCallback, useEffect, useState } from 'react';
import { Bot, Check, Folder, FolderX, TriangleAlert, X } from 'lucide-react';
import {
  api,
  ApiError,
  type AdminBot,
  type BotAccessMode,
  type BotsPayload,
  type UserSummary,
} from '../api/client.ts';
import { formatBotAudience } from '../lib/format.ts';
import { Avatar } from '../components/Avatar.tsx';
// The scrim and the card the app's dialogs are built from; the two sheets here
// are the same shape as GalleryLogDialog next door.
import '../components/NewChatDialog.css';
import './AdminBots.css';

/**
 * The «Боты» tab: who each bot is for, and which folder it sits in.
 *
 * Its own tab rather than more columns on «Интеграции», because the subject is
 * different. That table is about a token and what it has delivered; this is
 * about an account and who may talk to it — a question the assistants, which
 * have no token and never will, are equally subject to.
 *
 * The two do not repeat each other, even though both name people. «Кому
 * приходит» over there is the delivery roster, which is wider than access: a
 * bot delivers into every group it was put in, and a group's roster is nobody's
 * permission to grant. Access decides whose list the bot's own chat appears in.
 *
 * Loads on open and after each change, like AdminIntegrations and unlike the
 * overview: nothing here moves unless somebody moves it.
 */

const MODES: { id: BotAccessMode; title: string; hint: string }[] = [
  {
    id: 'admins',
    title: 'Только админ',
    hint: 'Как у нового бота. В списках у остальных его нет.',
  },
  {
    id: 'selected',
    title: 'Выбранные пользователи',
    hint: 'Бот появляется только у тех, кто в списке ниже.',
  },
  {
    id: 'everyone',
    title: 'Все пользователи',
    hint: 'Открыт всем, включая новых.',
  },
];

/** The state of a bot's access, readable without opening it. */
function AccessPill({ bot }: { bot: AdminBot }) {
  if (bot.mode === 'everyone') return <span className="bots-pill bots-pill-all">Все</span>;
  if (bot.mode === 'selected') {
    return <span className="bots-pill bots-pill-some">{bot.users.length} чел.</span>;
  }
  return <span className="bots-pill">Только админ</span>;
}

/**
 * Picking a folder, and making one.
 *
 * «Без группы» is the first row and an ordinary choice rather than a reset: a
 * bot outside every folder is a row of its own in the list, and «Прочее» is a
 * folder nobody wanted. A new folder is made with this bot already in it —
 * there is no screen where folders exist on their own, and there is not meant
 * to be one.
 */
function GroupSheet({
  bot,
  groups,
  onPick,
  onClose,
}: {
  bot: AdminBot;
  groups: { name: string; bots: number }[];
  onPick: (name: string | null) => void;
  onClose: () => void;
}) {
  const [fresh, setFresh] = useState('');

  useEffect(() => {
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', escape);
    return () => window.removeEventListener('keydown', escape);
  }, [onClose]);

  return (
    <div className="dialog-scrim" onMouseDown={onClose}>
      <div
        className="dialog-card bots-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Группа бота"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <h2 className="dialog-title">Группа</h2>

        <button
          type="button"
          className={`bots-sheet-row${bot.group === null ? ' bots-sheet-row-on' : ''}`}
          onClick={() => onPick(null)}
        >
          <FolderX size={18} />
          <span className="bots-sheet-body">
            <b>Без группы</b>
            <small>Плоская строка в списке ботов</small>
          </span>
          {bot.group === null && <Check className="bots-sheet-check" size={16} />}
        </button>

        {groups.map((group) => (
          <button
            type="button"
            key={group.name}
            className={`bots-sheet-row${bot.group === group.name ? ' bots-sheet-row-on' : ''}`}
            onClick={() => onPick(group.name)}
          >
            <Folder size={18} />
            <span className="bots-sheet-body">
              <b>{group.name}</b>
              <small>ботов: {group.bots}</small>
            </span>
            {bot.group === group.name && <Check className="bots-sheet-check" size={16} />}
          </button>
        ))}

        <form
          className="bots-sheet-new"
          onSubmit={(event) => {
            event.preventDefault();
            if (fresh.trim()) onPick(fresh.trim());
          }}
        >
          <input
            className="text-field"
            value={fresh}
            placeholder="Например, Сайт Оли"
            maxLength={40}
            onChange={(event) => setFresh(event.target.value)}
          />
          <button type="submit" className="primary-button admin-save" disabled={!fresh.trim()}>
            Создать
          </button>
        </form>
      </div>
    </div>
  );
}

/** «Добавить» — the search the handoff leaves undrawn, in the picker's shape. */
function UserSheet({
  taken,
  onPick,
  onClose,
}: {
  taken: string[];
  onPick: (user: UserSummary) => void;
  onClose: () => void;
}) {
  const [people, setPeople] = useState<UserSummary[] | null>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    // Failing silently: an empty picker says the same thing a message would,
    // and there is a nickname field on the other tab either way.
    void api.listUsers().then(setPeople, () => setPeople([]));
  }, []);

  useEffect(() => {
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', escape);
    return () => window.removeEventListener('keydown', escape);
  }, [onClose]);

  const needle = query.trim().toLowerCase();
  const shown = (people ?? [])
    .filter((user) => !user.bot && !taken.includes(user.id))
    .filter(
      (user) =>
        !needle ||
        user.displayName.toLowerCase().includes(needle) ||
        user.username.toLowerCase().includes(needle),
    );

  return (
    <div className="dialog-scrim" onMouseDown={onClose}>
      <div
        className="dialog-card bots-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Кому открыть бота"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <h2 className="dialog-title">Кому открыть</h2>
        <input
          className="text-field"
          value={query}
          placeholder="Имя или никнейм"
          autoFocus
          spellCheck={false}
          onChange={(event) => setQuery(event.target.value)}
        />
        <div className="bots-sheet-people">
          {shown.length === 0 ? (
            <p className="admin-note">{people === null ? '' : 'Никого не осталось'}</p>
          ) : (
            shown.map((user) => (
              <button
                type="button"
                key={user.id}
                className="bots-sheet-row"
                onClick={() => onPick(user)}
              >
                <Avatar
                  className="bots-person-avatar"
                  photoId={user.avatarPhotoId}
                  name={user.displayName}
                />
                <span className="bots-sheet-body">
                  <b>{user.displayName}</b>
                  <small>@{user.username}</small>
                </span>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export function AdminBots() {
  const [payload, setPayload] = useState<BotsPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [chosen, setChosen] = useState<string | null>(null);
  /** The unsaved state of the card: the mode and the list it is editing. */
  const [draft, setDraft] = useState<{ mode: BotAccessMode; users: AdminBot['users'] } | null>(null);
  /** The name being typed, kept apart from access: it saves on its own. */
  const [name, setName] = useState('');
  const [sheet, setSheet] = useState<'group' | 'people' | null>(null);

  const load = useCallback(async () => {
    try {
      setPayload(await api.listBots());
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось загрузить ботов');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const bots = payload?.bots ?? [];
  const bot = bots.find((row) => row.botUserId === chosen) ?? bots[0] ?? null;

  // Seeded from whichever bot is on screen, and re-seeded when that changes.
  // Not on every load: a reload after saving must not overwrite what somebody
  // has started typing into the card next to it.
  useEffect(() => {
    if (!bot) return;
    setDraft({ mode: bot.mode, users: bot.users });
    setName(bot.name);
  }, [bot]);

  async function change(what: string, run: () => Promise<BotsPayload>) {
    setBusy(true);
    setNotice(null);
    try {
      setPayload(await run());
      setNotice(what);
    } catch (err) {
      setNotice(err instanceof ApiError ? err.message : 'Не получилось');
    }
    setBusy(false);
  }

  if (error && !payload) return <div className="admin-error">{error}</div>;
  if (!payload) return <div className="admin-section" />;

  if (bots.length === 0) {
    return (
      <section className="admin-section">
        <h2 className="admin-section-title">Боты</h2>
        <p className="admin-note">
          Пока ни одного. Бот заводится вместе с интеграцией на соседней вкладке, а ассистенты
          появляются сами, когда в <code>.env</code> есть ключ.
        </p>
      </section>
    );
  }

  const mode = draft?.mode ?? bot?.mode ?? 'admins';
  const users = draft?.users ?? [];
  const hint =
    mode === 'everyone'
      ? 'Доступен всем пользователям'
      : mode === 'admins'
        ? 'Бот доступен только вам'
        : users.length === 0
          ? 'Бот доступен только вам'
          : formatBotAudience(users.length);

  return (
    <section className="admin-section">
      <h2 className="admin-section-title">Боты</h2>
      <p className="admin-note">
        Права живут на боте. Новый бот доступен только админу; открыть его можно списку
        пользователей или всем. Группа — папка бота в списке чатов на телефоне.
      </p>

      <div className="bots-split">
        <div className="bots-list">
          {bots.map((row) => (
            <button
              type="button"
              key={row.botUserId}
              className={`bots-row${row.botUserId === bot?.botUserId ? ' bots-row-on' : ''}`}
              onClick={() => setChosen(row.botUserId)}
            >
              <span className="bots-row-tile">
                <Bot size={18} />
              </span>
              <span className="bots-row-body">
                <b>{row.name}</b>
                <small>{row.group ?? (row.kind === 'ai' ? 'ассистент' : 'без группы')}</small>
              </span>
              <AccessPill bot={row} />
            </button>
          ))}
        </div>

        {bot && (
          <div className="bots-card">
            <div className="bots-card-head">
              <h3 className="bots-card-title">{bot.name}</h3>
              <span className="bots-card-handle">@{bot.username}</span>
            </div>

            <label className="admin-field bots-group">
              <span>Имя</span>
              <div className="bots-group-row">
                <input
                  className="bots-name"
                  value={name}
                  maxLength={64}
                  onChange={(event) => setName(event.target.value)}
                />
                <button
                  type="button"
                  className="settings-secondary"
                  disabled={busy || !name.trim() || name.trim() === bot.name}
                  onClick={() =>
                    void change(`Теперь «${name.trim()}»`, () =>
                      api.saveBotName(bot.botUserId, name.trim()),
                    )
                  }
                >
                  Переименовать
                </button>
              </div>
              <small>
                Как бота зовут у всех: в списке чатов, в шапке переписки и в уведомлении. Никнейм
                @{bot.username} остаётся прежним — по нему на бота ссылаются.
              </small>
            </label>

            <label className="admin-field bots-group">
              <span>Группа</span>
              <div className="bots-group-row">
                <input value={bot.group ?? 'Без группы'} readOnly />
                <button
                  type="button"
                  className="settings-secondary"
                  disabled={busy}
                  onClick={() => setSheet('group')}
                >
                  Изменить
                </button>
              </div>
              <small>
                Группа — папка бота на мобильном. «Сайт Оли» собирает заявки и отзывы в одну строку
                списка.
              </small>
            </label>

            <div className="bots-access" role="radiogroup" aria-label="Кому доступен">
              <span className="bots-access-label">Кому доступен</span>
              {MODES.map((option) => (
                <button
                  type="button"
                  key={option.id}
                  role="radio"
                  aria-checked={mode === option.id}
                  className={`bots-mode${mode === option.id ? ' bots-mode-on' : ''}`}
                  onClick={() => setDraft({ mode: option.id, users })}
                >
                  <span className={`bot-radio${mode === option.id ? ' bot-radio-on' : ''}`} />
                  <span className="bots-mode-text">
                    <b>{option.title}</b>
                    <small>{option.hint}</small>
                  </span>
                </button>
              ))}
            </div>

            {/* Not a block: saving is still allowed. Opening a bot to everybody
                is a decision somebody can mean, and this says what it costs. */}
            {mode === 'everyone' && (
              <p className="bots-warning">
                <TriangleAlert size={16} />
                Бот появится в списке у всех пользователей и будет принимать от них сообщения. Для
                служебных ботов это обычно не то, что нужно.
              </p>
            )}

            {mode === 'selected' && (
              <div className="bots-people">
                <div className="bots-people-head">
                  <span>Пользователи · {users.length}</span>
                  <button
                    type="button"
                    className="bots-add"
                    disabled={busy}
                    onClick={() => setSheet('people')}
                  >
                    Добавить
                  </button>
                </div>
                {users.length === 0 ? (
                  <p className="bots-people-empty">Пока никого. Бот виден только админу.</p>
                ) : (
                  <div className="bots-chips">
                    {users.map((user) => (
                      <span className="bots-chip" key={user.id}>
                        {user.displayName}
                        <button
                          type="button"
                          className="bots-chip-remove"
                          aria-label={`Убрать ${user.displayName}`}
                          onClick={() =>
                            setDraft({
                              mode,
                              users: users.filter((held) => held.id !== user.id),
                            })
                          }
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="admin-actions">
              <button
                type="button"
                className="primary-button admin-save"
                disabled={busy}
                onClick={() =>
                  void change('Сохранено', () =>
                    api.saveBotAccess(bot.botUserId, {
                      mode,
                      userIds: users.map((user) => user.id),
                    }),
                  )
                }
              >
                Сохранить
              </button>
              <span className="admin-notice">{notice ?? hint}</span>
            </div>
          </div>
        )}
      </div>

      {sheet === 'group' && bot && (
        <GroupSheet
          bot={bot}
          groups={payload.groups}
          onClose={() => setSheet(null)}
          onPick={(name) => {
            setSheet(null);
            void change(name === null ? 'Без группы' : `Группа: ${name}`, () =>
              api.saveBotGroup(bot.botUserId, name),
            );
          }}
        />
      )}

      {sheet === 'people' && bot && (
        <UserSheet
          taken={users.map((user) => user.id)}
          onClose={() => setSheet(null)}
          onPick={(user) => {
            setSheet(null);
            setDraft({
              mode,
              users: [
                ...users,
                { id: user.id, username: user.username, displayName: user.displayName },
              ],
            });
          }}
        />
      )}
    </section>
  );
}
